import type { ActionDefinition } from '@gzl10/nexus-sdk'
import type { Context } from 'hono'

/** Tables that must never be truncated */
const SYSTEM_TABLES = new Set([
  '_nexus_migrations',
  '_nexus_migration_lock',
  '_nexus_sequences',
  'sqlite_sequence',    // SQLite internal
  'knex_migrations',    // Knex default (unused but safe)
  'knex_migrations_lock'
])

/**
 * Standalone action - Factory Reset
 *
 * Truncates all data tables (preserving schema) and re-seeds
 * all modules with their default data.
 *
 * Protected by: CASL (ADMIN only) + double confirmation (type "RESET" + confirm dialog)
 */
export const factoryResetAction: ActionDefinition = {
  key: 'factory-reset',
  scope: 'module',
  group: { en: 'System', es: 'Sistema' },
  label: { en: 'Factory Reset', es: 'Reinicio de Fábrica' },
  icon: 'mdi:database-refresh',
  variant: 'danger',
  output: {},

  confirm: {
    type: 'double',
    title: { en: 'Factory Reset', es: 'Reinicio de Fábrica' },
    message: {
      en: 'This will DELETE ALL DATA and restore default values. This action cannot be undone.',
      es: 'Esto ELIMINARÁ TODOS LOS DATOS y restaurará los valores por defecto. Esta acción no se puede deshacer.'
    },
    verifyText: 'RESET',
    verifyLabel: {
      en: 'Type RESET to confirm',
      es: 'Escribe RESET para confirmar'
    },
    confirmText: { en: 'Reset Everything', es: 'Reiniciar Todo' },
    severity: 'error'
  },

  handler: async (ctx, _input, c?: Context<any>) => {
    const user = c?.get('user') as { id?: string } | undefined
    const userId = user?.id ?? 'unknown'
    const fwd = c?.req.header('x-forwarded-for')
    const ip = fwd?.split(',')[0]?.trim() || c?.req.header('x-real-ip')
    const userAgent = c?.req.header('user-agent')
    const knex = ctx.db.knex

    ctx.core.logger.warn({ userId }, 'Factory reset initiated')

    // Notify audit before reset (tables will be dropped); setImmediate lets async listeners flush
    ctx.events.notify('audit.log', {
      source: 'core:system',
      action: 'factory_reset',
      actorId: user?.id,
      ip,
      userAgent
    })
    await new Promise<void>(resolve => setImmediate(resolve))

    // 1. Get all user tables (exclude system infrastructure)
    const allTables = await getAllTables(knex)
    const dataTables = allTables.filter(t => !SYSTEM_TABLES.has(t))

    if (dataTables.length === 0) {
      return { success: true, message: 'No data tables found', tablesCleared: 0, modulesSeeded: 0 }
    }

    // 2. Truncate all data tables (disable FK checks for clean truncation)
    const client = knex.client.config.client as string
    const isSQLite = client === 'better-sqlite3' || client === 'sqlite3'
    const isPostgres = client === 'pg' || client === 'postgresql'

    if (isSQLite) {
      await knex.raw('PRAGMA foreign_keys = OFF')
    } else if (isPostgres) {
      // PostgreSQL: TRUNCATE ... CASCADE in a single statement
      const tableList = dataTables.map(t => `"${t}"`).join(', ')
      await knex.raw(`TRUNCATE TABLE ${tableList} CASCADE`)
    } else {
      // MySQL / others
      await knex.raw('SET FOREIGN_KEY_CHECKS = 0')
    }

    if (!isPostgres) {
      // SQLite and MySQL: delete table by table
      for (const table of dataTables) {
        try {
          await knex(table).del()
        } catch (err) {
          ctx.core.logger.warn({ table, err }, 'Failed to clear table, skipping')
        }
      }
    }

    // Reset sequences for SQLite
    if (isSQLite) {
      await knex.raw('PRAGMA foreign_keys = ON')
    } else if (!isPostgres) {
      await knex.raw('SET FOREIGN_KEY_CHECKS = 1')
    }

    // Also reset nexus sequences counter
    try {
      await knex('_nexus_sequences').del()
    } catch {
      // Table might not exist
    }

    ctx.core.logger.info({ tables: dataTables.length }, 'All data tables cleared')

    // 3. Re-seed all modules in dependency order
    const modules = ctx.engine.getModules()
    let modulesSeeded = 0

    for (const mod of modules) {
      try {
        const seeded = await ctx.db.seedModule(mod)
        if (seeded) modulesSeeded++
      } catch (err) {
        ctx.core.logger.error({ module: mod.name, err }, 'Seed failed during factory reset')
      }
    }

    ctx.core.logger.warn({ userId, tablesCleared: dataTables.length, modulesSeeded }, 'Factory reset completed')

    return {
      success: true,
      message: `Factory reset complete. ${dataTables.length} tables cleared, ${modulesSeeded} modules re-seeded.`,
      tablesCleared: dataTables.length,
      modulesSeeded
    }
  },

  casl: {
    subject: 'SystemManagement',
    permissions: {
      ADMIN: { actions: ['execute'] }
    }
  }
}

/**
 * Get all table names from the database
 */
async function getAllTables(knex: import('knex').Knex): Promise<string[]> {
  const client = knex.client.config.client as string

  if (client === 'better-sqlite3' || client === 'sqlite3') {
    const rows = await knex.raw("SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%'")
    return (rows as Array<{ name: string }>).map(r => r.name)
  }

  if (client === 'pg' || client === 'postgresql') {
    const result = await knex.raw(
      "SELECT tablename FROM pg_tables WHERE schemaname = 'public'"
    )
    return result.rows.map((r: { tablename: string }) => r.tablename)
  }

  // MySQL
  const result = await knex.raw('SHOW TABLES')
  const key = Object.keys(result[0]?.[0] ?? {})[0] as string
  if (!key) return []
  return (result[0] as Record<string, string>[]).map(r => r[key]).filter(Boolean) as string[]
}
